import {
    DataTable,
    DataTableCell,
    DataTablePresenter,
    getAdjustmentCode,
    getRemarkCode,
    ITableColumn,
    renderAmount,
    renderEmpty,
    renderString,
    renderStringGroup,
    ProviderClaimPaymentItem,
    ProviderClaimPaymentItemAdjustment,
    SupportClaimPaymentItem,
    SupportClaimPaymentItemAdjustment,
} from '../../../..';
import { Variables } from '../../../base';
import React, { useMemo } from 'react';
import { CellProps } from 'react-table';
import * as S from '../claim-timeline.styles';
import styled from '@emotion/styled/macro';
import _ from 'lodash';

// Summary component styles (mimics Support's Summary component for MuiPaper popover content)
const SummaryContainer = styled.section`
    min-width: 250px;
    padding: 20px 20px 0 20px;
`;

const SummaryItem = styled.div`
    margin-bottom: 24px;
`;

const SummaryGroupItem = styled.div`
    padding-top: 20px;
    border-bottom: 1px solid ${Variables.Colors.platinum};
    
    &:last-of-type {
        border-bottom: none;
    }
`;

const SummaryLabel = styled.div`
    font-size: 18px;
    color: ${Variables.Colors.n70};
`;

const SummaryValue = styled.div`
    font-size: 16px;
    line-height: 1.25;
    color: ${Variables.Colors.n100};
    margin-top: 8px;
    white-space: pre-wrap;
`;

interface ISummarySchema {
    accessor: string | ((value: any) => React.ReactElement | string | null);
    label?: string;
    schema?: ISummarySchema[];
}

// Simple Summary component (mimics Support's Summary for AsyncList popovers)
const SimpleSummaryItem: React.FunctionComponent<{
    schema: ISummarySchema;
    data: any;
}> = (props): React.ReactElement | null => {
    const { schema, data } = props;

    if (typeof schema.accessor === 'string') {
        const value = _.get(data, schema.accessor);
        const nestedSchema = schema.schema;

        if (!_.isNumber(value) && _.isEmpty(value)) {
            return null;
        }

        if (_.isUndefined(value)) {
            return null;
        }

        if (Array.isArray(nestedSchema)) {
            return (
                <SummaryItem key={schema.label}>
                    {schema.label && <SummaryLabel>{schema.label}</SummaryLabel>}
                    {(value as any[]).map((el, i) => {
                        return (
                            <SummaryGroupItem key={i}>
                                {nestedSchema.map(s => {
                                    return <SimpleSummaryItem data={el} schema={s} key={s.label} />;
                                })}
                            </SummaryGroupItem>
                        );
                    })}
                </SummaryItem>
            );
        }

        return (
            <SummaryItem key={schema.label}>
                {schema.label && <SummaryLabel>{schema.label}</SummaryLabel>}
                {Array.isArray(value) ? <SummaryValue>{value.join('\n')}</SummaryValue> : <SummaryValue>{value}</SummaryValue>}
            </SummaryItem>
        );
    }

    const value = schema.accessor(data);
    if (!value) {
        return null;
    }

    return (
        <SummaryItem key={schema.label}>
            {schema.label && <SummaryLabel>{schema.label}</SummaryLabel>}
            <SummaryValue>{value}</SummaryValue>
        </SummaryItem>
    );
};

const SimpleSummary: React.FunctionComponent<{ value: any; schema: ISummarySchema[] }> = (props): React.ReactElement => {
    const { value, schema } = props;

    return (
        <SummaryContainer>
            {schema.map((el, i) => {
                return <SimpleSummaryItem data={value} schema={el} key={i} />;
            })}
        </SummaryContainer>
    );
};

type PaymentItem = ProviderClaimPaymentItem | SupportClaimPaymentItem;
type PaymentItemAdjustment = ProviderClaimPaymentItemAdjustment | SupportClaimPaymentItemAdjustment;

interface IPaymentItemsProps {
    items: PaymentItem[];
    paymentAdjustments?: PaymentItemAdjustment[];
}

export const PaymentItems: React.FunctionComponent<IPaymentItemsProps> = React.memo(({ items, paymentAdjustments }) => {
    const columns: ITableColumn<PaymentItem>[] = useMemo<ITableColumn<PaymentItem>[]>(
        () => [
            {
                id: 'ControlNumber',
                Header: 'Control Number',
                accessor: 'controlNumber',
                Cell: DataTableCell('String'),
                disableSortBy: true,
            },
            {
                id: 'ProcedureCode',
                Header: 'Procedure Code',
                accessor: 'procedureCode',
                Cell: DataTableCell('String'),
                disableSortBy: true,
            },
            {
                id: 'ModifierCodes',
                Header: 'Modifier Codes',
                accessor: 'modifierCodes',
                Cell: DataTableCell('StringGroup'),
                disableSortBy: true,
            },
            {
                id: 'RemarkCodes',
                Header: 'Remark Codes',
                accessor: 'remarkCodes',
                Cell: (props: CellProps<PaymentItem>) => {
                    const remarkCodes = props.row.original.remarkCodes;
                    if (!remarkCodes?.length) {
                        return renderEmpty();
                    }

                    return DataTableCell('AsyncList', {
                        type: 'AsyncList',
                        loadMethod: async (): Promise<React.ReactElement> => {
                            const schema: ISummarySchema[] = [
                                {
                                    accessor: (item: PaymentItem) =>
                                        item.remarkCodes
                                            ? renderStringGroup(
                                                  item.remarkCodes.map(remarkCode => getRemarkCode(remarkCode)),
                                              )
                                            : renderEmpty(),
                                },
                            ];

                            return <SimpleSummary schema={schema} value={{ remarkCodes }} />;
                        },
                    })(props);
                },
                disableSortBy: true,
            },
            {
                id: 'ChargeAmount',
                Header: 'Charge amount',
                accessor: 'chargeAmount',
                Cell: DataTableCell('Amount'),
                disableSortBy: true,
            },
            {
                id: 'ChargeCount',
                Header: 'Unit',
                accessor: 'chargeCount',
                Cell: DataTableCell('String'),
                disableSortBy: true,
            },
            {
                id: 'AllowedAmount',
                Header: 'Allowed amount',
                accessor: 'allowedAmount',
                Cell: DataTableCell('Amount'),
                disableSortBy: true,
            },
            {
                id: 'PaidAmount',
                Header: 'Paid amount',
                accessor: 'paidAmount',
                Cell: DataTableCell('Amount'),
                disableSortBy: true,
            },
            {
                id: 'FromDOS',
                Header: 'From DOS',
                accessor: 'fromDos',
                Cell: DataTableCell('Date'),
                disableSortBy: true,
                width: 150,
            },
            {
                id: 'ThroughDOS',
                Header: 'Through DOS',
                accessor: 'throughDos',
                Cell: DataTableCell('Date'),
                disableSortBy: true,
                show: false,
                width: 150,
            },
            {
                id: 'PlaceOfServiceType',
                Header: 'Place of service code',
                accessor: 'placeOfServiceCode',
                Cell: DataTableCell('String'),
                disableSortBy: true,
                show: false,
            },
            {
                id: 'Adjustments',
                Header: 'Adjustments',
                accessor: (): string => 'Details',
                width: 200,
                Cell: (props: CellProps<PaymentItem>) => {
                    const adjustments = props.row.original.adjustments;
                    if (!adjustments?.length) {
                        return renderEmpty();
                    }

                    return DataTableCell('AsyncList', {
                        type: 'AsyncList',
                        loadMethod: async (): Promise<React.ReactElement> => {
                            const schema: ISummarySchema[] = [
                                {
                                    accessor: 'adjustments',
                                    schema: [
                                        {
                                            label: 'Group',
                                            accessor: 'group',
                                        },
                                        {
                                            label: 'Reason',
                                            accessor: (adjustment: PaymentItemAdjustment) =>
                                                adjustment.reasonCode
                                                    ? renderString(getAdjustmentCode(adjustment.reasonCode))
                                                    : renderEmpty(),
                                        },
                                        {
                                            label: 'Amount',
                                            accessor: (adjustment: PaymentItemAdjustment) =>
                                                renderAmount(adjustment.amount),
                                        },
                                        {
                                            label: 'Units',
                                            accessor: 'units',
                                        },
                                    ],
                                },
                            ];

                            return <SimpleSummary schema={schema} value={{ adjustments }} />;
                        },
                    })(props);
                },
                disableSortBy: true,
            },
        ],
        [],
    );

    const adjustmentColumns: ITableColumn<PaymentItemAdjustment>[] = useMemo<
        ITableColumn<PaymentItemAdjustment>[]
    >(
        () => [
            {
                id: 'AdjustmentGroup',
                Header: 'Group',
                accessor: 'group',
                Cell: DataTableCell('String'),
                disableSortBy: true,
            },
            {
                id: 'AdjustmentReason',
                Header: 'Reason',
                accessor: (adjustment: PaymentItemAdjustment) =>
                    adjustment.reasonCode ? getAdjustmentCode(adjustment.reasonCode) : '',
                Cell: DataTableCell('String'),
                disableSortBy: true,
            },
            {
                id: 'AdjustmentAmount',
                Header: 'Amount',
                accessor: 'amount',
                Cell: DataTableCell('Amount'),
                disableSortBy: true,
            },
            {
                id: 'AdjustmentUnits',
                Header: 'Units',
                accessor: 'units',
                Cell: DataTableCell('String'),
                disableSortBy: true,
            },
        ],
        [],
    );

    const { current: presenter } = React.useRef(
        new DataTablePresenter({
            name: 'payment-items',
            data: items,
            columns,
            filterSchemas: [],
        }),
    );

    const { current: adjustmentsPresenter } = React.useRef(
        new DataTablePresenter({
            name: 'payment-items-adjustments',
            data: paymentAdjustments || [],
            columns: adjustmentColumns,
            filterSchemas: [],
        }),
    );

    presenter.onDataUpdate(items);
    adjustmentsPresenter.onDataUpdate(paymentAdjustments || []);

    return (
        <>
            {!!paymentAdjustments?.length && (
                <>
                    <S.Subtitle>Adjustments</S.Subtitle>
                    <S.ResourceTable>
                        <DataTable
                            presenter={adjustmentsPresenter}
                            hidePagination={true}
                            hideFiltering={true}
                            isLoading={false}
                            hideSettings={true}
                        />
                    </S.ResourceTable>
                </>
            )}
            <S.Subtitle>Service Lines</S.Subtitle>
            <S.ResourceTable>
                <DataTable presenter={presenter} hidePagination={true} hideFiltering={true} isLoading={false} />
            </S.ResourceTable>
        </>
    );
});
